import { cn } from '@/utils/shad';
import { PropsWithChildren, ReactNode } from 'react';
import { WithOnClick } from '@/types/component';

type LoadingSpinnerProps = {
  className?: string;
  fullContainer?: boolean;
  size?: number;
};

export const LoadingSpinner = ({ className, fullContainer, size = 24 }: LoadingSpinnerProps) => {
  return (
    <div
      role="status"
      className={cn(
        fullContainer && 'absolute h-full',
        'z-10 flex w-full cursor-default items-center justify-center bg-black/10',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox={`0 0 24 24`}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn('animate-spin', className)}
      >
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
};

export const WithLoading = (
  props: React.PropsWithChildren<{ loading: boolean; hideChildren?: boolean; size?: number }>,
) => {
  const { loading, children } = props;

  return (
    <>
      {loading && <LoadingSpinner size={props.size} fullContainer={true} />}
      {children}
    </>
  );
};

type WithLoadingContainerProps = PropsWithChildren &
  WithOnClick & {
    loading: boolean;
    hideChildren?: boolean;
    className?: string;
    size?: number;
    absolute?: boolean;
    loadingClassName?: string;
    loadingComponent?: ReactNode;
  };

export const WithLoadingContainer = ({
  loading,
  children,
  loadingClassName,
  className,
  hideChildren,
  size,
  loadingComponent,
  absolute = true,
  onClick,
}: WithLoadingContainerProps) => {
  const hide = hideChildren && loading;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      className={cn('relative h-full w-full hover:cursor-auto', className, loading && loadingClassName)}
    >
      {loading &&
        (loadingComponent ? (
          loadingComponent
        ) : (
          <LoadingSpinner className="m-auto" fullContainer={absolute} size={size} />
        ))}
      {!hide && children}
    </div>
  );
};
